<template>
  <MoleculeFrameCard class="promo-code-block" spacing="sm">
    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <AtomText
        :text="t('subscription.informationsSummary.promo.title')"
        font-size="text-sm"
        font-style="font-medium"
      />
      <div class="flex items-center gap-2">
        <MoleculeInputText
          id="promotion-code"
          v-model="currentPromotionCode"
          name="promotion-code"
          class="w-full"
          :placeholder="t('subscription.informationsSummary.promo.placeholder')"
          :disabled="isApplyPromotionCodeInProgress || isRemovePromotionCodeInProgress"
          :input-state="
            promotionCodeError ? getInputState({ errorMessage: promotionCodeError }) : 'info'
          "
          data-cy="promo-code-input"
          @keydown.enter.prevent="handleApplyPromotionCode"
        />
        <MoleculeButton
          variant="secondary"
          size="md"
          :label="t('subscription.informationsSummary.promo.apply')"
          :disabled="
            !currentPromotionCode ||
            isApplyPromotionCodeInProgress ||
            isRemovePromotionCodeInProgress
          "
          :loading="isApplyPromotionCodeInProgress"
          data-cy="promo-code-apply"
          @click="handleApplyPromotionCode"
        />
      </div>
    </div>

    <template v-if="promotionCode">
      <AtomDivider class="my-3" />
      <div class="flex items-center justify-between gap-2" data-cy="promo-code-applied">
        <div class="flex items-center gap-2">
          <AtomIcon
            icon-name="CheckCircleIcon"
            icon-type="solid"
            icon-size="sm"
            color-variant="success"
          />
          <div class="flex flex-col">
            <AtomText
              :text="promotionCode"
              font-size="text-sm"
              font-style="font-semibold"
              color-variant="success"
            />
          </div>
        </div>
        <MoleculeButton
          icon-name="TrashIcon"
          :label="t('subscription.informationsSummary.promo.remove')"
          icon-position="left"
          size="md"
          variant="minimal"
          :disabled="isRemovePromotionCodeInProgress"
          :loading="isRemovePromotionCodeInProgress"
          data-cy="promo-code-remove"
          @click="handleRemovePromotionCode"
        />
      </div>
    </template>

    <div
      v-else-if="promotionCodeError"
      class="mt-2 flex items-center gap-1"
      data-cy="promo-code-error"
    >
      <AtomIcon icon-name="XCircleIcon" icon-type="solid" icon-size="sm" color-variant="error" />
      <AtomText :text="promotionCodeError" color-variant="error" font-size="text-sm" />
    </div>
  </MoleculeFrameCard>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import useInputState from '@/composables/useInputState.composable';
import { useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import { getErrorToasterConfig, matchErrorCode } from '@/utils/error.util';

import { EPromotionCodeErrorType } from '@/constants/errors.contants';

const { t } = useI18n();
const { getInputState } = useInputState();
const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const { promotionCode, isApplyPromotionCodeInProgress, isRemovePromotionCodeInProgress } =
  storeToRefs(subscriptionStore);

const currentPromotionCode = ref(promotionCode.value ?? '');

const promotionCodeError = ref<string | null>(null);

const handleApplyPromotionCode = async () => {
  if (!currentPromotionCode.value) {
    return;
  }

  try {
    promotionCodeError.value = null;
    await subscriptionStore.applyPromotionCode(currentPromotionCode.value);
  } catch (error) {
    const errorCode = matchErrorCode(error, EPromotionCodeErrorType);
    if (errorCode === EPromotionCodeErrorType.PROMOTION_CODE_INVALID) {
      promotionCodeError.value = t('subscription.informationsSummary.promo.invalid');
      return;
    }
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'promotion-code-error',
      defaultMessage: t('subscription.informationsSummary.api.errors.applyPromotionCode'),
    });
    toastSnackBar.addToast(toastConfig);
  }
};

const handleRemovePromotionCode = async () => {
  try {
    await subscriptionStore.removePromotionCode();
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'promotion-code-error',
      defaultMessage: t('subscription.informationsSummary.api.errors.removePromotionCode'),
    });
    toastSnackBar.addToast(toastConfig);
  } finally {
    promotionCodeError.value = null;
  }
  currentPromotionCode.value = '';
};
</script>
